/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { Formik, yupToFormErrors, Field as FormikField } from 'formik'
import DatePicker from 'react-datepicker'
import moment from 'moment'
import cloneDeep from 'lodash/cloneDeep'
import ValidationSchema from './EditFormValidation'
import { TYPE } from './EditFormConstants'
import { useServiceContext } from '../../../../root'
import styled from 'styled-components'
import Modal from '../../../../components/Modal'
import { Select, Creatable } from '../../../../components/Select'
import Field from '../../../../components/shared/Field'
import { ErrorMessageStyled } from '../../../../components/shared/ErrorMessage'
import Loader from '../../../../components/shared/Loader'
import 'react-datepicker/dist/react-datepicker.css'

const HalfWidthDiv = styled.div`
  width: 50%;
  float: left;
`

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 15px;
  &.w100p {
    width: 100%;
  }
  &.w80p {
    width: 80%;
  }
`

interface EditFormProps {
  onSubmit: (values: any, actions: any) => Promise<any>
  doClose: () => void
  isEdit: boolean
  property: any
  availableTypes: any[]
  users: any[]
  groups: any[]
}

const EditForm: React.FC<EditFormProps> = ({ onSubmit, doClose, isEdit, property, availableTypes, users, groups }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const validationSchema = ValidationSchema()

  const transformFromServer = (metadata: any) => {
    if (!metadata) return null

    // Remove null values
    const cleanMetadata = JSON.parse(JSON.stringify(metadata, (key, value) => (value === null ? undefined : value)))

    if (cleanMetadata && cleanMetadata.definition) {
      // Transform user/group IDs for SELECT_ONE_USER/GROUP
      if (
        cleanMetadata.definition.defaultValue &&
        (cleanMetadata.type === TYPE.SELECT_ONE_USER || cleanMetadata.type === TYPE.SELECT_ONE_GROUP)
      ) {
        cleanMetadata.definition.defaultValue = cleanMetadata.definition.defaultValue.id
      }

      // Transform user/group IDs for SELECT_MULTI_USER/GROUP
      if (
        cleanMetadata.definition.defaultValues &&
        (cleanMetadata.type === TYPE.SELECT_MULTI_USER || cleanMetadata.type === TYPE.SELECT_MULTI_GROUP)
      ) {
        cleanMetadata.definition.defaultValues = cleanMetadata.definition.defaultValues.map((item: any) => item.id)
      }

      // Transform datetime to moment object
      if (cleanMetadata.definition.defaultValue && cleanMetadata.type === TYPE.DATETIME) {
        const momentDate = moment(cleanMetadata.definition.defaultValue).utc()
        momentDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        cleanMetadata.definition.defaultValue = momentDate.toDate()
      }
    }

    return cleanMetadata
  }

  const transformToSelectOptions = (arr: string[]) => {
    if (Array.isArray(arr)) {
      return arr.map((val) => ({
        value: val,
        label: val
      }))
    }
    return []
  }

  const getDefaults = (vals: any) => {
    const defaults = validationSchema.cast(vals, {
      recursive: true,
      stripUnknown: true,
      context: vals
    })
    return defaults
  }

  const [initialValues, setInitialValues] = useState(() => {
    const transformed = transformFromServer(property)
    const defaults = getDefaults({ metadata: transformed || {} }).metadata

    // Ensure all fields used in Select components are defined (not undefined)
    // This prevents React's "uncontrolled to controlled" warning
    if (!defaults.type) {
      defaults.type = ''
    }
    if (!defaults.definition) {
      defaults.definition = {}
    }
    if (defaults.definition.defaultValue === undefined) {
      defaults.definition.defaultValue = null
    }
    if (defaults.definition.defaultValues === undefined) {
      defaults.definition.defaultValues = null
    }
    if (defaults.definition.options === undefined) {
      defaults.definition.options = []
    }

    return {
      metadata: defaults,
      options: transformToSelectOptions(transformed?.definition?.options || [])
    }
  })

  useEffect(() => {
    if (isEdit && property && Object.keys(property).length > 0) {
      const transformed = transformFromServer(property)
      const defaults = getDefaults({ metadata: transformed || {} }).metadata

      // Ensure all fields used in Select components are defined (not undefined)
      // This prevents React's "uncontrolled to controlled" warning
      if (!defaults.type) {
        defaults.type = ''
      }
      if (!defaults.definition) {
        defaults.definition = {}
      }
      if (defaults.definition.defaultValue === undefined) {
        defaults.definition.defaultValue = null
      }
      if (defaults.definition.defaultValues === undefined) {
        defaults.definition.defaultValues = null
      }
      if (defaults.definition.options === undefined) {
        defaults.definition.options = []
      }

      setInitialValues({
        metadata: defaults,
        options: transformToSelectOptions(transformed?.definition?.options || [])
      })
    }
  }, [property, isEdit])

  const closeDialog = () => {
    if (doClose) {
      doClose()
    }
  }

  const handleSubmit = (values: any, actions: any) => {
    const finalData = getDefaults(values)
    const metadata = cloneDeep(finalData.metadata)

    // Transform datetime back to timestamp
    if (metadata.type === TYPE.DATETIME && metadata.definition && metadata.definition.defaultValue) {
      const dateValue = moment(metadata.definition.defaultValue).utc(true)
      dateValue.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      metadata.definition.defaultValue = dateValue.valueOf()
    }

    return onSubmit(metadata, actions)
  }

  return (
    <div>
      <Formik
        validate={(values) => {
          return validationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => yupToFormErrors(err)
            )
        }}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnBlur={false}
      >
        {({ values, setFieldValue, handleSubmit: formikHandleSubmit, isSubmitting }) => (
          <div>
            <Loader blocking={(isEdit && Object.keys(property).length === 0) || isSubmitting}>
              <Modal.Body>
                <form className={'clearfix'}>
                  <HalfWidthDiv>
                    {/* Name */}
                    <StyledFormGroup controlId="5f2e5dfd-34c9-4220-8775-17f7d54d91b6">
                      <Form.Label>{translate('METADATA_FORM_LABEL_NAME')}</Form.Label>
                      <FormikField
                        type="text"
                        name="metadata.name"
                        maxLength={255}
                        autoFocus
                        className="form-control field-control"
                        id="5f2e5dfd-34c9-4220-8775-17f7d54d91b6"
                      />
                      <ErrorMessageStyled name="metadata.name" />
                    </StyledFormGroup>

                    {/* Description */}
                    <StyledFormGroup controlId="f1db725e-91c7-4629-8568-f09436af9f31">
                      <Form.Label>{translate('METADATA_FORM_LABEL_DESCRIPTION')}</Form.Label>
                      <FormikField
                        type="text"
                        name="metadata.description"
                        maxLength={255}
                        className="form-control field-control"
                        id="f1db725e-91c7-4629-8568-f09436af9f31"
                      />
                      <ErrorMessageStyled name="metadata.description" />
                    </StyledFormGroup>

                    {/* Type */}
                    <StyledFormGroup controlId="48f1448b-f458-45a0-8ee5-7ed883578708">
                      <Form.Label>{translate('METADATA_FORM_LABEL_TYPE')}</Form.Label>
                      <Select
                        name="metadata.type"
                        id="48f1448b-f458-45a0-8ee5-7ed883578708"
                        placeholder={translate('METADATA_FORM_PLACEHOLDER_TYPE')}
                        isClearable={false}
                        value={values.metadata.type}
                        options={availableTypes}
                        onChange={(option: any) => {
                          setFieldValue('metadata.type', option ? option.value : null)
                          setFieldValue('metadata.definition.defaultValue', null)
                          setFieldValue('metadata.definition.defaultValues', null)

                          // Preserve options if switching between SELECT_MULTI and SELECT_ONE
                          if (
                            !(
                              [TYPE.SELECT_MULTI, TYPE.SELECT_ONE].includes(values.metadata.type) &&
                              [TYPE.SELECT_MULTI, TYPE.SELECT_ONE].includes(option?.value)
                            )
                          ) {
                            setFieldValue('metadata.definition.options', [])
                            setFieldValue('options', [])
                          }
                        }}
                        className="field-control"
                      />
                      <ErrorMessageStyled name="metadata.type" />
                    </StyledFormGroup>

                    {/* List of Values (for SELECT_ONE and SELECT_MULTI) */}
                    {[TYPE.SELECT_ONE, TYPE.SELECT_MULTI].includes(values.metadata.type) && (
                      <StyledFormGroup controlId="0069e36c-06f2-49c2-a9c5-82c9158ed4b9">
                        <Form.Label>{translate('METADATA_FORM_LIST_OF_VALUES')}</Form.Label>
                        <Creatable
                          value={values.metadata.definition.options || []}
                          options={values.options || []}
                          onChange={(options: any) => {
                            if (Array.isArray(options)) {
                              const optionsKeys = options.map((o) => o.value)

                              // Clean up defaultValues for MULTI
                              if (values.metadata.type === TYPE.SELECT_MULTI) {
                                const cleanedValues = (values.metadata.definition.defaultValues || []).filter(
                                  (val: string) => optionsKeys.includes(val)
                                )
                                setFieldValue(
                                  'metadata.definition.defaultValues',
                                  cleanedValues.length > 0 ? cleanedValues : null
                                )
                              }

                              // Clean up defaultValue for ONE
                              if (values.metadata.type === TYPE.SELECT_ONE) {
                                if (
                                  values.metadata.definition.defaultValue &&
                                  !optionsKeys.includes(values.metadata.definition.defaultValue)
                                ) {
                                  setFieldValue('metadata.definition.defaultValue', null)
                                }
                              }

                              setFieldValue('options', options)
                            }

                            setFieldValue(
                              'metadata.definition.options',
                              options ? options.map((o: any) => o.value) : []
                            )
                          }}
                          isMulti={true}
                          className="field-control"
                          id="0069e36c-06f2-49c2-a9c5-82c9158ed4b9"
                        />
                        <ErrorMessageStyled name="metadata.definition.options" />
                      </StyledFormGroup>
                    )}

                    {/* Default Value(s) */}
                    {values.metadata.type && values.metadata.type !== TYPE.ARTIFACT && (
                      <StyledFormGroup controlId="d2ae0e66-0a3f-46ab-90d4-38c25488b444">
                        <Form.Label>
                          {translate(
                            [TYPE.SELECT_MULTI, TYPE.SELECT_MULTI_USER, TYPE.SELECT_MULTI_GROUP].includes(
                              values.metadata.type
                            )
                              ? 'METADATA_FORM_LABEL_DEFAULT_VALUE(S)'
                              : 'METADATA_FORM_LABEL_DEFAULT_VALUE'
                          )}
                        </Form.Label>
                        <div>
                          {/* DATETIME */}
                          {values.metadata.type === TYPE.DATETIME && (
                            <DatePicker
                              selected={values.metadata.definition.defaultValue}
                              onChange={(date: Date | null) => {
                                setFieldValue('metadata.definition.defaultValue', date)
                              }}
                              dateFormat="MMM dd, yyyy"
                              showYearDropdown
                              className="form-control field-control"
                              id="d2ae0e66-0a3f-46ab-90d4-38c25488b444"
                            />
                          )}

                          {/* STRING */}
                          {values.metadata.type === TYPE.STRING && (
                            <FormikField
                              type="text"
                              name="metadata.definition.defaultValue"
                              maxLength={255}
                              className="form-control field-control"
                              id="d2ae0e66-0a3f-46ab-90d4-38c25488b444"
                            />
                          )}

                          {/* NUMBER */}
                          {values.metadata.type === TYPE.NUMBER && (
                            <input
                              type="text"
                              onChange={(ev) => {
                                if (ev.target.value && /^[0-9]*$/.test(ev.target.value)) {
                                  setFieldValue(
                                    'metadata.definition.defaultValue',
                                    ev.target.value.length < 16
                                      ? ev.target.value
                                      : values.metadata.definition.defaultValue
                                  )
                                } else {
                                  setFieldValue(
                                    'metadata.definition.defaultValue',
                                    ev.target.value ? values.metadata.definition.defaultValue : null
                                  )
                                }
                              }}
                              name="metadata.definition.defaultValue"
                              value={values.metadata.definition.defaultValue || ''}
                              className="field-control form-control"
                              id="d2ae0e66-0a3f-46ab-90d4-38c25488b444"
                            />
                          )}

                          {/* SELECT_MULTI */}
                          {values.metadata.type === TYPE.SELECT_MULTI && (
                            <Select
                              isMulti={true}
                              options={values.options}
                              onChange={(option: any) => {
                                setFieldValue(
                                  'metadata.definition.defaultValues',
                                  option ? option.map((o: any) => o.value) : []
                                )
                              }}
                              value={values.metadata.definition.defaultValues}
                              className="field-control"
                              id="d2ae0e66-0a3f-46ab-90d4-38c25488b444"
                            />
                          )}

                          {/* SELECT_ONE */}
                          {values.metadata.type === TYPE.SELECT_ONE && (
                            <Select
                              isMulti={false}
                              options={values.options}
                              onChange={(option: any) => {
                                setFieldValue('metadata.definition.defaultValue', option ? option.value : null)
                              }}
                              value={values.metadata.definition.defaultValue}
                              className="field-control"
                              id="d2ae0e66-0a3f-46ab-90d4-38c25488b444"
                            />
                          )}

                          {/* SELECT_ONE_USER */}
                          {values.metadata.type === TYPE.SELECT_ONE_USER && (
                            <Select
                              name="metadata.definition.defaultValue"
                              options={users}
                              onChange={(option: any) => {
                                setFieldValue('metadata.definition.defaultValue', option ? option.value : null)
                              }}
                              value={values.metadata.definition.defaultValue}
                              placeholder={translate('SELECT_USER...')}
                              className="field-control"
                              id="d2ae0e66-0a3f-46ab-90d4-38c25488b444"
                            />
                          )}

                          {/* SELECT_MULTI_USER */}
                          {values.metadata.type === TYPE.SELECT_MULTI_USER && (
                            <Select
                              name="metadata.definition.defaultValues"
                              options={users}
                              onChange={(option: any) => {
                                setFieldValue(
                                  'metadata.definition.defaultValues',
                                  option ? option.map((o: any) => o.value) : []
                                )
                              }}
                              value={values.metadata.definition.defaultValues}
                              placeholder={translate('SELECT_USER...')}
                              isMulti={true}
                              className="field-control"
                              id="d2ae0e66-0a3f-46ab-90d4-38c25488b444"
                            />
                          )}

                          {/* SELECT_ONE_GROUP */}
                          {values.metadata.type === TYPE.SELECT_ONE_GROUP && (
                            <Select
                              name="metadata.definition.defaultValue"
                              options={groups}
                              onChange={(option: any) => {
                                setFieldValue('metadata.definition.defaultValue', option ? option.value : null)
                              }}
                              value={values.metadata.definition.defaultValue}
                              placeholder={translate('SELECT_GROUP...')}
                              className="field-control"
                              id="d2ae0e66-0a3f-46ab-90d4-38c25488b444"
                            />
                          )}

                          {/* SELECT_MULTI_GROUP */}
                          {values.metadata.type === TYPE.SELECT_MULTI_GROUP && (
                            <Select
                              name="metadata.definition.defaultValues"
                              options={groups}
                              onChange={(option: any) => {
                                setFieldValue(
                                  'metadata.definition.defaultValues',
                                  option ? option.map((o: any) => o.value) : []
                                )
                              }}
                              value={values.metadata.definition.defaultValues}
                              placeholder={translate('SELECT_GROUP...')}
                              isMulti={true}
                              className="field-control"
                              id="d2ae0e66-0a3f-46ab-90d4-38c25488b444"
                            />
                          )}
                        </div>

                        {[TYPE.SELECT_MULTI, TYPE.SELECT_MULTI_USER, TYPE.SELECT_MULTI_GROUP].includes(
                          values.metadata.type
                        ) ? (
                          <ErrorMessageStyled name="metadata.definition.defaultValues" />
                        ) : (
                          <ErrorMessageStyled name="metadata.definition.defaultValue" />
                        )}
                      </StyledFormGroup>
                    )}

                    {/* Read Only Checkbox */}
                    {(!isEdit || (isEdit && values.metadata.readOnly)) && (
                      <StyledFormGroup controlId="24f4b74e-b7cc-4f14-a5ff-7872bd3ea967">
                        <div className="checkbox">
                          <label htmlFor="24f4b74e-b7cc-4f14-a5ff-7872bd3ea967">
                            <FormikField
                              type="checkbox"
                              name="metadata.readOnly"
                              checked={values.metadata.readOnly}
                              onChange={(ev: any) => {
                                setFieldValue('metadata.readOnly', ev.target.checked)
                              }}
                              disabled={isEdit && property?.readOnly}
                              id="24f4b74e-b7cc-4f14-a5ff-7872bd3ea967"
                            />
                            <b>{translate('METADATA_FORM_READ_ONLY')}</b>
                          </label>
                        </div>
                      </StyledFormGroup>
                    )}
                  </HalfWidthDiv>
                </form>
              </Modal.Body>
              <Modal.Footer>
                <button
                  type="button"
                  data-action={'cancel'}
                  className="btn btn-chita btn-link"
                  onClick={closeDialog}
                  id="41addebd-1f39-493b-8049-bc41420a1514"
                >
                  {translate('CANCEL')}
                </button>
                <button
                  type="button"
                  data-action={'save'}
                  className="btn btn-primary text-capitalize"
                  onClick={(e) => {
                    e.preventDefault()
                    formikHandleSubmit()
                  }}
                  disabled={isSubmitting}
                  id="bf92e0d6-a49f-468e-8de8-cb40c57ac25e"
                >
                  {translate('SAVE')}
                </button>
              </Modal.Footer>
            </Loader>
          </div>
        )}
      </Formik>
    </div>
  )
}

export default EditForm
